/**
 * Student inspiration reader (/portal/ideas/:id).
 * Anti-copy ChapterReader for content + downloadable attachments.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  DownloadSimple,
  Paperclip,
} from "@phosphor-icons/react";
import { inspirationsApi } from "../../api/inspirations";
import { monthLabel } from "../../lib/dates";
import ChapterReader from "../../components/courses/ChapterReader";
import FileIcon from "../../components/resources/FileIcon";
import { fileKind, humanFileSize } from "../../api/resources";

export default function PortalInspirationReader() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await inspirationsApi.get(id);
      setArticle(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [load]);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !article) {
    return <p className="text-sm text-red-600">{t("inspiration.not_found")}</p>;
  }
  const isEmpty =
    !article.content?.content || article.content.content.length === 0;

  return (
    <div data-testid="portal-inspiration-reader-page" className="space-y-8 max-w-3xl mx-auto">
      <Link
        to="/portal/ideas"
        data-testid="portal-inspiration-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("portal.nav.ideas")}
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 capitalize">
          {monthLabel(article.month, t)} {article.year}
        </p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {article.title}
        </h1>
      </header>

      <article className="rounded-3xl border border-slate-200 bg-white px-6 md:px-10 py-8 md:py-12">
        {isEmpty ? (
          <p className="text-sm text-slate-500">
            {t("inspiration.empty_article")}
          </p>
        ) : (
          <ChapterReader value={article.content} />
        )}
      </article>

      {article.attachments.length > 0 && (
        <section data-testid="portal-inspiration-attachments" className="space-y-3">
          <h2 className="font-heading text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Paperclip size={16} weight="bold" />
            {t("inspiration.attachments")}
          </h2>
          <div className="space-y-2">
            {article.attachments.map((att) => {
              const kind = fileKind(att.mime_type, att.original_filename);
              return (
                <div
                  key={att.id}
                  data-testid={`portal-inspiration-attachment-${att.id}`}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                    <FileIcon kind={kind} size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {att.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {humanFileSize(att.size)}
                    </p>
                  </div>
                  <a
                    href={inspirationsApi.attachmentDownloadUrl(att.id)}
                    data-testid={`portal-inspiration-attachment-download-${att.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  >
                    <DownloadSimple size={13} weight="bold" />
                    {t("resources.download")}
                  </a>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
